import React, { useState } from 'react';
import { Download, Package, Eye, FileText, CheckCircle, Loader2, AlertCircle } from 'lucide-react';
import { usePatternStudio } from '../context/PatternStudioContext';
import { downloadPDF, downloadEtsyZip, generateWatermarkedPDF } from '../export/pdfExport';

const EXPORT_TYPES = [
  {
    value: 'pdf',
    icon: FileText,
    title: 'PDF Only',
    description: 'Clean PDF for buyers to download. Includes all pages, photos, and branding.',
    recommended: false,
  },
  {
    value: 'zip',
    icon: Package,
    title: 'Etsy ZIP Package',
    description: 'Full ZIP with: main PDF + watermarked preview + Etsy listing text with tags.',
    recommended: true,
  },
  {
    value: 'watermark',
    icon: Eye,
    title: 'Watermarked Preview',
    description: 'Blurred preview PDF for social media or as a free Etsy sample.',
    recommended: false,
  },
];

const FORMATS = [
  { value: 'a4',     label: 'A4',     sub: '210 × 297mm — Europe, global standard' },
  { value: 'letter', label: 'Letter', sub: '216 × 279mm — US/Canada standard' },
];

function ValidationItem({ ok, label }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-gray-600' : 'text-amber-600'}`}>
      {ok ? <CheckCircle size={14} className="text-green-500 flex-shrink-0" /> : <AlertCircle size={14} className="flex-shrink-0" />}
      {label}
    </div>
  );
}

export default function Step8Export() {
  const { state, save } = usePatternStudio();
  const [exportType, setExportType] = useState('zip');
  const [format, setFormat] = useState('a4');
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState('idle'); // idle | generating | done | error
  const [error, setError] = useState('');

  const p = state.pattern;
  const hasTitle = !!p.title;
  const hasMaterials = (p.materials || []).length > 0;
  const hasSections = (p.sections || []).length > 0;
  const hasCover = (p.photos || []).some(ph => ph.category === 'cover');
  const hasBranding = !!p.branding?.shop_name;

  async function handleExport() {
    if (!hasTitle) { setError('Please add a pattern name first.'); return; }

    setStatus('generating');
    setProgress(0);
    setError('');

    try {
      // Auto-save first
      await save(true);

      if (exportType === 'pdf') {
        await downloadPDF(p, { format, onProgress: setProgress });
      } else if (exportType === 'zip') {
        await downloadEtsyZip(p, { format, onProgress: setProgress });
      } else if (exportType === 'watermark') {
        const pdf = await generateWatermarkedPDF(p, { format, onProgress: setProgress });
        const slug = (p.title || 'pattern').toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        pdf.save(`${slug}-preview.pdf`);
      }

      setStatus('done');
    } catch (e) {
      setStatus('error');
      setError(e.message || 'Export failed. Please try again.');
    }
  }

  return (
    <div className="p-6 max-w-xl">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Export</h2>
      <p className="text-sm text-gray-500 mb-6">
        Generate your Etsy-ready PDF. This may take a moment depending on the number of photos.
      </p>

      {/* Checklist */}
      <div className="p-4 bg-stone-50 rounded-2xl border border-stone-100 mb-6">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Pattern Checklist</p>
        <div className="space-y-2">
          <ValidationItem ok={hasTitle}     label="Pattern name added" />
          <ValidationItem ok={hasMaterials} label="Materials list filled" />
          <ValidationItem ok={hasSections}  label="Pattern sections added" />
          <ValidationItem ok={hasCover}     label="Cover photo uploaded" />
          <ValidationItem ok={hasBranding}  label="Shop name added" />
        </div>
      </div>

      {/* Export type */}
      <div className="space-y-3 mb-6">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide">Export Format</p>
        {EXPORT_TYPES.map(t => {
          const Icon = t.icon;
          const active = exportType === t.value;
          return (
            <button
              key={t.value}
              onClick={() => setExportType(t.value)}
              className={`w-full flex items-start gap-4 p-4 rounded-2xl border text-left transition-all ${
                active ? 'border-stone-400 bg-stone-50' : 'border-gray-200 bg-white hover:border-stone-300'
              }`}
            >
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${active ? 'bg-stone-700' : 'bg-gray-100'}`}>
                <Icon size={16} className={active ? 'text-white' : 'text-gray-500'} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{t.title}</p>
                  {t.recommended && (
                    <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white" style={{ background: '#6B5744' }}>
                      Recommended
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{t.description}</p>
              </div>
            </button>
          );
        })}
      </div>

      {/* Paper format */}
      <div className="mb-6">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-3">Paper Size</p>
        <div className="grid grid-cols-2 gap-3">
          {FORMATS.map(f => (
            <button
              key={f.value}
              onClick={() => setFormat(f.value)}
              className={`px-4 py-3 rounded-xl border text-left transition-all ${
                format === f.value ? 'border-stone-400 bg-stone-50' : 'border-gray-200 bg-white hover:border-stone-300'
              }`}
            >
              <div className="text-sm font-semibold text-gray-900">{f.label}</div>
              <div className="text-xs text-gray-400 mt-0.5">{f.sub}</div>
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-xs text-red-600">
          <AlertCircle size={13} />
          {error}
        </div>
      )}

      {/* Progress */}
      {status === 'generating' && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs text-gray-500">Generating PDF…</span>
            <span className="text-xs font-semibold text-gray-700">{progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, #6B5744, #8B7355)' }}
            />
          </div>
        </div>
      )}

      {/* Done state */}
      {status === 'done' && (
        <div className="flex items-center gap-2 mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-xs text-green-700">
          <CheckCircle size={14} />
          Your file has been downloaded successfully!
        </div>
      )}

      {/* Main CTA */}
      <button
        onClick={handleExport}
        disabled={status === 'generating' || !hasTitle}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-white font-semibold text-sm shadow-lg hover:shadow-xl transition-all active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
        style={{ background: 'linear-gradient(135deg, #6B5744 0%, #8B7355 100%)' }}
      >
        {status === 'generating' ? (
          <><Loader2 size={16} className="animate-spin" /> Generating…</>
        ) : (
          <><Download size={16} /> Generate &amp; Download</>
        )}
      </button>

      {status === 'done' && (
        <button
          onClick={() => setStatus('idle')}
          className="w-full mt-2 py-2.5 rounded-2xl border border-stone-300 text-sm text-gray-700 hover:bg-stone-50 transition-all"
        >
          Export Again
        </button>
      )}
    </div>
  );
}
